import { useEffect } from 'react';
import useWindow from './useWindow';

export const createQueries = <T>(
  searchParameters: T,
  keyArray: Array<keyof T>,
  window?: Window | null
): void => {
  const newUrl = new URL(window?.location?.href || '');
  const urlSearchParams = keyArray.reduce((total, current) => {
    return { ...total, [current]: searchParameters[current] };
  }, {});
  newUrl.searchParams.delete('productId');
  Object.entries(urlSearchParams).forEach(([key, value]) => {
    newUrl.searchParams.delete(key);
    if (value !== null && value !== undefined) {
      newUrl.searchParams.append(key, (value as string) || '');
    }
  });
  window?.history?.pushState({}, '', newUrl.href);
};

export const removeQueries = (window?: Window | null): void => {
  const newUrl = new URL(window?.location?.href || '');
  window?.history?.pushState({}, '', `${newUrl.origin}${newUrl.pathname}`);
};

export const doesParameterExist = (key: string, window?: Window | null): boolean => {
  const url = new URL(window?.location?.href || '');
  return url.searchParams.has(key);
};

export const getParams = <T>(
  keyArray: (keyof T)[],
  href?: string,
  window?: Window | null
): Record<keyof T, string> => {
  const url = new URL(href || window?.location?.href || '');
  return keyArray.reduce((total, current) => {
    return url.searchParams.get(current as string)
      ? { ...total, [current]: url.searchParams.get(current as string) }
      : { ...total };
  }, {}) as Record<keyof T, string>;
};

const useHandleQueries = <T extends Record<keyof T, string>>(
  keyArray: Array<keyof T>,
  mainKey: keyof T,
  popStateCallback: () => void,
  mountCallback: (params: Record<keyof T, string>) => void,
  additionalDeps?: unknown[],
  mountCallbackCondition?: boolean
): void => {
  const window = useWindow();

  useEffect(() => {
    const handlePopstate = (): void => {
      const url = new URL(window?.location?.href || '');
      if (!url.searchParams.has(mainKey as string)) {
        popStateCallback();
      }
    };
    window?.removeEventListener('popstate', handlePopstate);
    window?.addEventListener('popstate', handlePopstate);

    const params = getParams(keyArray, undefined, window);

    if (params[mainKey] && (mountCallbackCondition === undefined || mountCallbackCondition)) {
      mountCallback(params);
    }

    return (): void => {
      window?.removeEventListener('popstate', handlePopstate);
    };
    // eslint-disable-next-line
  }, [popStateCallback, mountCallback, mountCallbackCondition, ...(additionalDeps || [])]);
};

export default useHandleQueries;
